import React, { useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination, Keyboard, A11y } from 'swiper/modules';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Captions from 'yet-another-react-lightbox/plugins/captions';

export interface CarouselItem {
  src: string;
  alt: string;
  title?: string;
  description?: string;
  badge?: string;
}

interface MediaCarouselProps {
  items: CarouselItem[];
  autoplayDelay?: number;
  className?: string;
  rounded?: boolean;
  pinkTheme?: boolean;
  showDots?: boolean;
}

const MediaCarousel: React.FC<MediaCarouselProps> = ({
  items,
  autoplayDelay = 3500,
  className = '',
  rounded = true,
  pinkTheme = false,
  showDots = true,
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const lightboxSlides = useMemo(
    () =>
      items.map((item) => ({
        src: item.src,
        description: item.description,
        alt: item.alt,
        title: item.title,
      })),
    [items]
  );

  return (
    <div className={`media-carousel ${pinkTheme ? 'media-carousel--pink' : ''} ${className}`}>
      <Swiper
        modules={[Autoplay, Navigation, Pagination, Keyboard, A11y]}
        slidesPerView={1}
        loop
        autoplay={{ delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: true }}
        navigation
        pagination={showDots ? { clickable: true } : false}
        keyboard={{ enabled: true }}
        onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        className="media-carousel__swiper"
      >
        {items.map((item, index) => (
          <SwiperSlide key={`${item.src}-${index}`}>
            <div
              className={`media-carousel__card ${rounded ? 'media-carousel__card--rounded' : ''}`}
              role="figure"
              aria-label={item.title || item.alt}
            >
              <button
                type="button"
                className="media-carousel__image-button"
                onClick={() => {
                  setActiveIndex(index);
                  setIsLightboxOpen(true);
                }}
                aria-label={`Ouvrir ${item.title || item.alt} en plein écran`}
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  loading="lazy"
                  className="media-carousel__image"
                />
              </button>
              <div className="media-carousel__content">
                {item.badge && <span className="media-carousel__badge">{item.badge}</span>}
                <div className="media-carousel__text">
                  {item.title && <h4 className="media-carousel__title">{item.title}</h4>}
                  {item.description && (
                    <p className="media-carousel__description">{item.description}</p>
                  )}
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <Lightbox
        open={isLightboxOpen}
        close={() => setIsLightboxOpen(false)}
        slides={lightboxSlides}
        index={activeIndex}
        plugins={[Zoom, Captions]}
        carousel={{ finite: false }}
        controller={{ closeOnPullUp: true, closeOnPullDown: true }}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null,
        }}
        animation={{ fade: 300, zoom: 300 }}
      />
    </div>
  );
};

export default MediaCarousel;
